import React, { useEffect, useMemo } from "react";
import { Icon, divIcon, latLngBounds } from "leaflet";
import { Clock, ExternalLink, Navigation } from "lucide-react";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { Circle, MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { Button } from "./ui/button";
import { buildMapsNavigationUrl, hasValidCoordinate } from "../lib/maps";
import { useLanguage } from "../context/LanguageContext";

Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

function createRestaurantPinIcon(dishCount, isActive) {
  if (typeof divIcon !== "function") return undefined;
  const size = isActive ? 36 : 30;
  const bg = isActive ? "#3D4035" : "#E07A5F";
  const border = isActive ? "3px solid #E07A5F" : "2px solid #FFFFFF";
  return divIcon({
    className: "restaurant-pin-icon",
    html: `<div style="width:${size}px;height:${size}px;border-radius:9999px;background:${bg};color:#FFFFFF;border:${border};display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px;box-shadow:0 2px 6px rgba(0,0,0,0.22);">${dishCount}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

function createUserLocationIcon() {
  if (typeof divIcon !== "function") return undefined;
  return divIcon({
    className: "user-location-pin-icon",
    html: `<div style="width:18px;height:18px;border-radius:9999px;background:#3B82F6;border:3px solid #FFFFFF;box-shadow:0 0 0 4px rgba(59,130,246,0.25);"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

function FitRestaurantBounds({ restaurants, userLocation }) {
  const map = useMap();

  useEffect(() => {
    if (!restaurants.length) return;

    const points = restaurants.map((restaurant) => [restaurant.lat, restaurant.lng]);
    if (hasValidCoordinate(userLocation?.lat, userLocation?.lng)) {
      points.push([userLocation.lat, userLocation.lng]);
    }

    const bounds = latLngBounds(points);
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [28, 28], maxZoom: 15 });
    }
  }, [map, restaurants, userLocation]);

  return null;
}

export default function RestaurantMap({ restaurants = [], userLocation, radiusKm = 2, activeRestaurant, onShowDetails }) {
  const { t } = useLanguage();
  const restaurantsWithCoordinates = useMemo(
    () => restaurants.filter((restaurant) => hasValidCoordinate(restaurant.lat, restaurant.lng)),
    [restaurants]
  );
  const hasUserCoords = hasValidCoordinate(userLocation?.lat, userLocation?.lng);
  const radiusMeters = Number.isFinite(Number(radiusKm)) && Number(radiusKm) > 0 ? Number(radiusKm) * 1000 : 2000;

  if (restaurantsWithCoordinates.length === 0) {
    return (
      <div
        className="bg-white border border-dashed border-[rgba(61,64,53,0.15)] rounded-2xl p-12 text-center text-[#6A6F5C]"
        data-testid="map-empty-state"
      >
        {t.mapNoRestaurants || "Keine Restaurants mit Kartenkoordinaten für diese Auswahl."}
      </div>
    );
  }

  const center = [restaurantsWithCoordinates[0].lat, restaurantsWithCoordinates[0].lng];

  return (
    <div className="bg-white border border-[rgba(61,64,53,0.08)] rounded-2xl overflow-hidden shadow-sm" data-testid="restaurant-map">
      <MapContainer center={center} zoom={13} scrollWheelZoom={false} className="h-[520px] w-full z-0">
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitRestaurantBounds restaurants={restaurantsWithCoordinates} userLocation={userLocation} />
        {hasUserCoords && Circle && (
          <Circle
            center={[userLocation.lat, userLocation.lng]}
            radius={radiusMeters}
            pathOptions={{ color: "#E07A5F", fillColor: "#E07A5F", fillOpacity: 0.08, weight: 1.5 }}
            data-testid="map-radius-circle"
          />
        )}
        {hasUserCoords && (
          <Marker
            position={[userLocation.lat, userLocation.lng]}
            icon={createUserLocationIcon()}
            zIndexOffset={500}
            data-testid="map-user-location"
          >
            <Popup>
              <div className="text-xs font-medium text-[#3D4035]">
                {t.yourLocation || "Your location"}
              </div>
            </Popup>
          </Marker>
        )}
        {restaurantsWithCoordinates.map((restaurant) => {
          const id = restaurant.source_id || restaurant.restaurant_name;
          const isActive = id === activeRestaurant;
          const navigationUrl = buildMapsNavigationUrl(restaurant);
          const dishCount = restaurant.specials?.length ?? 0;
          return (
            <Marker
              key={id}
              position={[restaurant.lat, restaurant.lng]}
              icon={createRestaurantPinIcon(dishCount, isActive)}
              zIndexOffset={isActive ? 1000 : 0}
              data-testid={`map-marker-${id}`}
            >
              <Popup>
                <div className="min-w-[180px] space-y-2">
                  <div>
                    <p className="font-heading font-semibold text-[#3D4035] leading-tight">{restaurant.restaurant_name}</p>
                    {restaurant.address && <p className="text-xs text-[#6A6F5C] mt-1">{restaurant.address}</p>}
                  </div>
                  <p className="text-xs text-[#6A6F5C]">
                    {t.dishes(restaurant.specials.length)}
                    {restaurant.distance_km != null ? ` · ${restaurant.distance_km.toFixed(1)} km` : ""}
                  </p>
                  {restaurant.opening_hours && (
                    <p className="inline-flex items-center gap-1 text-xs text-[#6A6F5C]" data-testid={`map-opening-hours-${id}`}>
                      <Clock size={11} aria-hidden="true" />
                      {restaurant.opening_hours}
                    </p>
                  )}
                  <Button
                    type="button"
                    className="rounded-full bg-[#E07A5F] hover:bg-[#cf6b51] text-white h-8 px-3 text-xs"
                    onClick={() => onShowDetails?.(id)}
                    data-testid={`map-details-${id}`}
                  >
                    {t.details || "Details"}
                  </Button>
                  {navigationUrl && (
                    <a
                      href={navigationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t.navigateToRestaurant(restaurant.restaurant_name)}
                      title={t.navigateToRestaurant(restaurant.restaurant_name)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#3D4035] text-[#F7F5F0] shadow-sm shadow-[#3D4035]/10 transition hover:-translate-y-0.5 hover:bg-[#2c2f25] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E07A5F]/40"
                      data-testid={`map-navigation-${id}`}
                    >
                      <Navigation size={14} strokeWidth={2.4} aria-hidden="true" />
                    </a>
                  )}
                  {restaurant.source_url && (
                    <a
                      href={restaurant.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 inline-flex h-8 items-center gap-1 rounded-full border border-[rgba(61,64,53,0.12)] px-3 text-xs font-semibold text-[#E07A5F] hover:bg-[#FBFAF6]"
                      data-testid={`map-website-${id}`}
                    >
                      {t.website}
                      <ExternalLink size={12} aria-hidden="true" />
                    </a>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
